import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Power,
  Lightbulb,
  Sun,
  Moon,
  Home,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getSmartHomeSettings,
  WebhookButton,
  addWebhookButton,
  updateWebhookButton,
} from "@/lib/smartHomeSettings";

// URL validation to prevent SSRF attacks
const isValidSmartHomeUrl = (url: string): { valid: boolean; error?: string } => {
  try {
    const parsedUrl = new URL(url);
    
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return { valid: false, error: "Only HTTP and HTTPS protocols are allowed" };
    }

    if (parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1') {
      return { valid: false, error: "Localhost URLs are not allowed for webhooks" };
    }

    const blockedHosts = ['169.254.169.254', 'metadata.google.internal', 'metadata.azure.com'];
    if (blockedHosts.some(host => parsedUrl.hostname.includes(host))) {
      return { valid: false, error: "This URL is not allowed" };
    }

    if (parsedUrl.pathname.includes('/latest/meta-data') || 
        parsedUrl.pathname.includes('/metadata/') ||
        parsedUrl.pathname.includes('/computeMetadata/')) {
      return { valid: false, error: "Metadata endpoints are not allowed" };
    }

    return { valid: true };
  } catch {
    return { valid: false, error: "Invalid URL format" };
  }
};

export default function SmartHomeButtonEdit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buttonId = searchParams.get("id");
  const isEditing = !!buttonId;
  const { t, dir } = useTranslation();

  const buttonIcons = [
    { id: "power", icon: Power, label: t("lightsOn") },
    { id: "lightbulb", icon: Lightbulb, label: t("brightness") },
    { id: "sun", icon: Sun, label: t("lightMode") },
    { id: "moon", icon: Moon, label: t("darkMode") },
    { id: "home", icon: Home, label: t("home") },
    { id: "play", icon: Play, label: t("startGame") },
  ];

  const buttonColors = [
    { id: "teal", bg: "bg-primary/20", border: "border-primary", text: "text-primary" },
    { id: "orange", bg: "bg-orange-500/20", border: "border-orange-500", text: "text-orange-500" },
    { id: "blue", bg: "bg-blue-500/20", border: "border-blue-500", text: "text-blue-500" },
    { id: "green", bg: "bg-green-500/20", border: "border-green-500", text: "text-green-500" },
    { id: "purple", bg: "bg-purple-500/20", border: "border-purple-500", text: "text-purple-500" },
    { id: "pink", bg: "bg-pink-500/20", border: "border-pink-500", text: "text-pink-500" },
    { id: "yellow", bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-600" },
    { id: "red", bg: "bg-red-500/20", border: "border-red-500", text: "text-red-500" },
  ];

  const [buttonData, setButtonData] = useState({
    name: "",
    icon: "power",
    color: "teal",
    webhookUrl: "",
    method: "POST" as "GET" | "POST",
  });

  // Load existing button if editing
  useEffect(() => {
    if (buttonId) {
      const settings = getSmartHomeSettings();
      const existing = settings.webhookButtons.find(b => b.id === buttonId);
      if (existing) {
        setButtonData({
          name: existing.name,
          icon: existing.icon,
          color: existing.color,
          webhookUrl: existing.webhookUrl,
          method: existing.method,
        });
      }
    }
  }, [buttonId]);

  const saveButton = () => {
    if (!buttonData.name.trim()) {
      toast.error(t("fillNameAndUrl"));
      return;
    }
    if (!buttonData.webhookUrl.trim()) {
      toast.error(t("fillNameAndUrl"));
      return;
    }

    const validation = isValidSmartHomeUrl(buttonData.webhookUrl);
    if (!validation.valid) {
      toast.error(validation.error || t("errorTitle"));
      return;
    }

    if (isEditing && buttonId) {
      updateWebhookButton(buttonId, buttonData);
      toast.success(t("buttonUpdated"));
    } else {
      addWebhookButton(buttonData);
      toast.success(t("buttonAdded"));
    }
    navigate("/smarthome");
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background p-6" dir={dir}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate("/smarthome")}
            className="p-3 rounded-2xl"
            data-scan-item
          >
            <ArrowRight className="w-8 h-8" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">
            {isEditing ? t("editButton") : t("newButton")}
          </h1>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-lg mx-auto w-full space-y-6"
        >
          <div className="bg-card rounded-3xl border-2 border-border p-8 space-y-6">
            {/* Name Input */}
            <div className="space-y-2">
              <Label>{t("buttonName")}</Label>
              <Input
                value={buttonData.name}
                onChange={(e) => setButtonData((p) => ({ ...p, name: e.target.value }))}
                placeholder={t("buttonName")}
                className="h-14 text-lg"
                data-scan-item
              />
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <Label>{t("webhookUrl")}</Label>
              <Input
                value={buttonData.webhookUrl}
                onChange={(e) => setButtonData((p) => ({ ...p, webhookUrl: e.target.value }))}
                placeholder="https://hooks.zapier.com/..."
                dir="ltr"
                className="h-14 text-lg"
                data-scan-item
              />
              <p className="text-xs text-muted-foreground">
                {t("webhookButtons")}
              </p>
            </div>

            {/* Method Select */}
            <div className="space-y-2">
              <Label>{t("methodLabel")}</Label>
              <Select
                value={buttonData.method}
                onValueChange={(v) => setButtonData((p) => ({ ...p, method: v as "GET" | "POST" }))}
              >
                <SelectTrigger className="h-14 text-lg" data-scan-item>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label>{t("iconLabel")}</Label>
              <div className="flex flex-wrap gap-3">
                {buttonIcons.map((icon) => {
                  const Icon = icon.icon;
                  return (
                    <button
                      key={icon.id}
                      onClick={() => setButtonData((p) => ({ ...p, icon: icon.id }))}
                      className={cn(
                        "w-14 h-14 rounded-xl border-2 flex items-center justify-center transition-all",
                        buttonData.icon === icon.id
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      )}
                      data-scan-item
                    >
                      <Icon className="w-7 h-7" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <Label>{t("colorLabel")}</Label>
              <div className="flex flex-wrap gap-3">
                {buttonColors.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setButtonData((p) => ({ ...p, color: color.id }))}
                      className={cn(
                        "w-12 h-12 rounded-lg border-2 transition-all",
                        color.bg,
                        color.border,
                        buttonData.color === color.id && "ring-2 ring-offset-2 ring-primary"
                      )}
                      data-scan-item
                    />
                  ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={saveButton}
                size="lg"
                className="flex-1 h-16 text-lg rounded-2xl"
                data-scan-item
              >
                {isEditing ? t("saveChanges") : t("addButton")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate("/smarthome")}
                className="h-16 text-lg rounded-2xl px-8"
                data-scan-item
              >
                {t("cancelBtn")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
}
